/* eslint-disable react/no-danger */
import React, { useEffect, useCallback, useState } from 'react';
import {
  ORGHeader,
  ATMSkipLinks,
  ATMLoader,
  MOLIdleTime,
  ATMSystemMessage,
  ATMLabel,
} from 'shared-it-appmod-ui';
import { useUtilContext } from 'src/contexts/util.context';
import { routes } from 'src/router';
import {
  logout,
  getAccount,
  getUser,
  fetchUserData,
} from 'src/libraries/auth.library';
import Moment from 'src/libraries/moment.library';
import Lang from 'src/libraries/language';
import { AccessRole } from 'src/constants';
import { setIsAdmin, hasAccess } from 'src/libraries/access.library';
import { useAdminContext } from 'src/contexts/admin.context';
import { useSettingContext } from 'src/contexts/setting.context';
import { useNotificationsContext } from 'src/contexts/notification.context';
import styles from './private.module.scss';

const PrivateLayout: React.FC = ({ children }) => {
  const [ready, setReady] = useState(false);
  const { actions } = useAdminContext();
  const { actions: settingActions } = useSettingContext();
  const { state: notificationState, actions: notificationActions } =
    useNotificationsContext();
  const { state: utilState, actions: utilActions } = useUtilContext();

  const data = [
    {
      id: 'navigation',
      text: 'Skip to navigation',
    },
    {
      id: 'content',
      text: 'Skip to content',
    },
  ];

  const account = getAccount();

  const fetchAdminAccess = useCallback(
    async (userId) => {
      await settingActions.listGET();

      setReady(false);
      const result = await actions.dataGET(userId);

      if (result.payload) {
        const user = result.payload;

        setIsAdmin(!!user, user.roleId === AccessRole.SUPER_ADMIN);
      } else {
        setIsAdmin(false);
      }

      setReady(true);
    },
    [setReady, actions, settingActions]
  );

  useEffect(() => {
    let timer;

    notificationActions.countGET();

    if (ready) {
      timer = setInterval(() => {
        notificationActions.countGET();
      }, 120000);
    }

    return () => {
      clearInterval(timer);
    };
  }, [ready, notificationActions]);

  const userData = getUser();

  useEffect(() => {
    if (account) {
      const checkUser = async () => {
        const fetchUser = async () => {
          const user = await fetchUserData();

          if (user) {
            await fetchAdminAccess(user.emp_no);
          }
        };

        const user = getUser();

        // Checks if user is stored in localStorage
        if (user && user.lan_user_id) {
          await fetchAdminAccess(user.emp_no);
        } else {
          fetchUser();
        }
      };

      checkUser();
      utilActions.footerGET();
      utilActions.notifGET();
    }
  }, [fetchAdminAccess, account, utilActions]);

  const idleTimeInMinutes: number = parseFloat(
    process.env.REACT_APP_IDLE_TIMEOUT_MINUTES ?? ''
  );

  return (
    <>
      {!ready ? (
        <ATMLoader active>{Lang.LBL_LOADING_REPORT_INFORMATION}</ATMLoader>
      ) : (
        <div className={styles.mainWrapper}>
          <ATMSkipLinks data={data} />
          {utilState.notification.notification &&
          Moment().isBetween(
            Moment(utilState.notification.notification[0]?.StartTime),
            Moment(utilState.notification.notification[0]?.EndTime)
          ) ? (
            <ATMSystemMessage
              notificationType={
                utilState.notification.notification[0]?.MessageType ?? ''
              }
              header={utilState.notification.notification[0]?.MessageHeader}
              content={utilState.notification.notification[0]?.MessageBody}
            />
          ) : null}
          <div className={styles.contentArea}>
            <ORGHeader
              logoName="socal"
              application={Lang.TTL_APPLICATION}
              environment={process.env.REACT_APP_ENVIRONMENT}
              menus={routes
                .filter((value) => value.label)
                .filter(
                  (value) =>
                    value.permission ? hasAccess(value.permission) : true // Removes menu items that has no access
                )
                .map((route) => {
                  if (route.label === Lang.TTL_NOTIFICATIONS) {
                    return {
                      name: route.label,
                      to: route.path,
                      content: (
                        <span className={styles.notification}>
                          {Lang.TTL_NOTIFICATIONS}{' '}
                          {notificationState.count > 0 && (
                            <ATMLabel
                              key={`counter_${notificationState.count}`}
                              color="red"
                              floating
                              circular
                            >
                              {notificationState.count}
                            </ATMLabel>
                          )}
                        </span>
                      ),
                    };
                  }

                  return {
                    name: route.label,
                    to: route.path,
                  };
                })}
              user={
                userData?.first_name
                  ? {
                      username: `${userData.first_name}`,
                    }
                  : undefined
              }
              onLogout={() => logout()}
            >
              <div className={styles.wrapper}>{children}</div>
            </ORGHeader>
          </div>
          <MOLIdleTime timeout={idleTimeInMinutes} onLogout={() => logout()} />
          {utilState.footer.footer && utilState.footer.footer[0]?.message && (
            <div
              dangerouslySetInnerHTML={{
                __html: utilState.footer.footer[0]?.message,
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default PrivateLayout;
